import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { safeStorage } from '../services/safeStorage';
import { 
  Home,
  Users, 
  UserX, 
  Truck, 
  FileText, 
  Upload, 
  LogOut, 
  Menu, 
  X,
  School,
  LayoutDashboard,
  UserPlus,
  UserCog,
  Settings as SettingsIcon,
  Search,
  Shield,
  Database,
  Calendar,
  Smartphone
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { AcademicYearSelector } from './AcademicYearSelector';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface LayoutProps {
  children: React.ReactNode;
  activeTab: string;
  setActiveTab: (tab: string, reportType?: string) => void;
  userRole: string | null;
}

const Layout: React.FC<LayoutProps> = ({ children, activeTab, setActiveTab, userRole }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      // Update UI to notify the user they can add to home screen
      setShowInstallButton(true);
    });

    window.addEventListener('appinstalled', () => {
      // Log install to analytics
      console.log('PWA was installed');
      setShowInstallButton(false);
    });
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    // Show the prompt
    deferredPrompt.prompt();
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);
    // We've used the prompt, and can't use it again, throw it away
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  useEffect(() => {
    const fetchMaintenance = async () => {
      try {
        const settingsRef = doc(db, 'systemSettings', 'general');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          setMaintenanceMode(settingsSnap.data().maintenanceMode || false);
        }
      } catch (e) {
        console.warn("Error fetching maintenance mode in layout:", e);
      }
    };
    fetchMaintenance();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    safeStorage.removeItem('bypass_auth_user');
    signOut(auth).finally(() => {
      window.location.reload();
    });
  };

  const menuItems = [
    { id: 'dashboard', label: 'עמוד הבית', icon: Home, adminOnly: true, color: 'text-indigo-500', bgColor: 'bg-indigo-50' },
    { id: 'attendance', label: 'נוכחות תלמידים', icon: Users, color: 'text-blue-500', bgColor: 'bg-blue-50' },
    { id: 'staff-edu', label: 'היעדרויות צוות חינוכי טיפולי', icon: UserX, adminOnly: true, color: 'text-emerald-500', bgColor: 'bg-emerald-50' },
    { id: 'staff-assist', label: 'היעדרויות צוות סייעות', icon: UserX, adminOnly: true, color: 'text-teal-500', bgColor: 'bg-teal-50' },
    { id: 'transport', label: 'נוכחות מסיעים', icon: Truck, adminOnly: true, color: 'text-amber-500', bgColor: 'bg-amber-50' },
    { id: 'reports', label: 'דוחות', icon: FileText, adminOnly: true, color: 'text-violet-500', bgColor: 'bg-violet-50' },
    { id: 'holidays', label: 'חופשים', icon: Calendar, adminOnly: true, color: 'text-rose-500', bgColor: 'bg-rose-50' },
    { id: 'manage-carriers', label: 'ניהול מסיעים', icon: Truck, adminOnly: true, color: 'text-indigo-500', bgColor: 'bg-indigo-50' },
    { id: 'manage-students', label: 'ניהול תלמידים', icon: UserPlus, adminOnly: true, color: 'text-sky-500', bgColor: 'bg-sky-50' },
    { id: 'manage-staff-edu', label: 'ניהול צוות חינוכי', icon: UserCog, adminOnly: true, color: 'text-fuchsia-500', bgColor: 'bg-fuchsia-50' },
    { id: 'manage-staff-assist', label: 'ניהול סייעות', icon: UserCog, adminOnly: true, color: 'text-pink-500', bgColor: 'bg-pink-50' },
    { id: 'classes', label: 'ניהול כיתות', icon: School, adminOnly: true, color: 'text-orange-500', bgColor: 'bg-orange-50' },
    { id: 'user-management', label: 'ניהול משתמשים', icon: Shield, adminOnly: true, color: 'text-red-500', bgColor: 'bg-red-50' },
    { id: 'backups', label: 'גיבוי נתונים', icon: Database, adminOnly: true, color: 'text-slate-500', bgColor: 'bg-slate-50' },
    { id: 'import', label: 'ייבוא נתונים', icon: Upload, adminOnly: true, color: 'text-gray-500', bgColor: 'bg-gray-50' },
    { id: 'settings', label: 'הגדרות מערכת', icon: SettingsIcon, adminOnly: true, color: 'text-zinc-500', bgColor: 'bg-zinc-50' },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (!item.adminOnly) return true;
    if (userRole === 'admin') return true;
    if (userRole === 'manager') {
      // Management team can see everything except settings, user management, backups, and import
      return !['user-management', 'settings', 'backups', 'import'].includes(item.id);
    }
    if (userRole === 'holiday_manager' && item.id === 'holidays') return true;
    return false;
  });

  if (!user) return <>{children}</>;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900" dir="rtl">
      {/* Sidebar for Desktop */}
      <aside className="fixed inset-y-0 right-0 hidden w-72 bg-white border-l border-slate-200 lg:block shadow-[0_0_20px_rgba(0,0,0,0.02)]">
        <div className="flex flex-col h-full">
          <button 
            onClick={() => setActiveTab('dashboard')}
            className="p-8 border-b border-slate-50 flex flex-col items-center gap-4 w-full hover:bg-slate-50/50 transition-all group"
          >
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-full opacity-0 group-hover:opacity-20 blur transition-opacity duration-500" />
              <img 
                src="/logo.png" 
                alt="School Logo" 
                className="h-24 w-auto object-contain relative"
              />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-black tracking-tight text-slate-800">בי"ס אלמגד</h1>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 mt-1">Al-Majd School Management</p>
            </div>
          </button>

          {/* Desktop Status Indicator */}
          <div className="px-8 py-4 border-b border-slate-50 space-y-2">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <span>סטטוס מערכת</span>
              <div className="flex items-center gap-1.5 text-emerald-500">
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                <span>Online</span>
              </div>
            </div>
            {maintenanceMode && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 text-amber-700 rounded-xl text-[10px] font-bold border border-amber-100">
                <Shield size={12} />
                <span>מצב תחזוקה פעיל</span>
              </div>
            )}
          </div>

          <nav className="flex-1 p-6 space-y-1.5 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200">
            {filteredMenuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={cn(
                  "group flex items-center w-full gap-3.5 px-4 py-3 rounded-2xl transition-all duration-300 relative overflow-hidden",
                  activeTab === item.id 
                    ? "bg-slate-900 text-white shadow-lg shadow-slate-200 translate-x-1" 
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                )}
              >
                {activeTab === item.id && (
                  <motion.div 
                    layoutId="activeTab"
                    className="absolute inset-0 bg-slate-900 -z-10"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <div className={cn(
                  "p-2 rounded-xl transition-colors duration-300",
                  activeTab === item.id ? "bg-white/10" : item.bgColor
                )}>
                  <item.icon size={18} className={cn(
                    "transition-colors duration-300",
                    activeTab === item.id ? "text-white" : item.color
                  )} />
                </div>
                <span className="text-sm font-semibold tracking-tight">{item.label}</span>
                {activeTab === item.id && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-indigo-500 rounded-r-full" />
                )}
              </button>
            ))}
          </nav>

          <div className="p-6 border-t border-slate-50 bg-slate-50/30">
            {showInstallButton && (
              <button
                onClick={handleInstallClick}
                className="flex items-center w-full gap-3 px-4 py-3 mb-3 bg-indigo-600 text-white rounded-2xl transition-all duration-300 font-bold text-sm shadow-lg shadow-indigo-100 animate-bounce"
              >
                <div className="p-2 bg-white/20 rounded-xl">
                  <Smartphone size={16} />
                </div>
                <span>התקן אפליקציה</span>
              </button>
            )}
            <div className="flex items-center gap-3 px-4 py-3 mb-3 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold shadow-md shadow-indigo-100">
                {(user.email?.[0] || "U").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-slate-800 truncate uppercase tracking-wider">{user.email?.split('@')[0]}</p>
                <p className="text-[10px] font-medium text-slate-400 truncate">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center w-full gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all duration-300 font-bold text-sm group"
            >
              <div className="p-2 bg-red-50 text-red-500 rounded-xl group-hover:bg-red-500 group-hover:text-white transition-all duration-300">
                <LogOut size={16} />
              </div>
              <span>התנתקות מהמערכת</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden bg-white/80 backdrop-blur-md border-b border-slate-100 h-20 flex items-center justify-between px-6 sticky top-0 z-50 shadow-sm">
        <button 
          onClick={() => setActiveTab('dashboard')}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <img 
            src="/logo.png" 
            alt="School Logo" 
            className="h-12 w-auto object-contain"
          />
          <div className="flex flex-col">
            <span className="font-black text-slate-800 text-base leading-tight">בי"ס אלמגד</span>
            <span className="text-[8px] uppercase tracking-widest font-bold text-slate-400">Management System</span>
          </div>
        </button>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <AcademicYearSelector compact />
          {maintenanceMode && (
            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 bg-amber-100 text-amber-700 rounded-lg text-[10px] font-bold border border-amber-200">
              <Shield size={12} />
              <span>תחזוקה</span>
            </div>
          )}
          <button
            onClick={handleLogout}
            title="התנתקות מהמערכת וחזרה למסך כניסה"
            className="p-2 text-rose-500 bg-rose-50 hover:bg-rose-100 rounded-xl transition-colors border border-rose-100"
          >
            <LogOut size={18} />
          </button>
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2.5 text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors border border-slate-100"
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm lg:hidden"
            />
            
            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 right-0 z-50 w-72 bg-white shadow-2xl lg:hidden flex flex-col"
            >
              <div className="p-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                <button 
                  onClick={() => {
                    setActiveTab('dashboard');
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                >
                  <img 
                    src="/logo.png" 
                    alt="School Logo" 
                    className="h-10 w-auto object-contain"
                  />
                  <div className="flex flex-col">
                    <span className="font-black text-slate-800 text-sm leading-tight">בי"ס אלמגד</span>
                    <span className="text-[7px] uppercase tracking-widest font-bold text-slate-400">Management System</span>
                  </div>
                </button>
                <button 
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-3 border-b border-slate-50 bg-slate-50/50">
                <AcademicYearSelector />
              </div>

              <nav className="flex-1 overflow-y-auto p-6 space-y-1.5">
                {filteredMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setIsMenuOpen(false);
                    }}
                    className={cn(
                      "group flex items-center w-full gap-3.5 px-4 py-3 rounded-2xl transition-all duration-300 relative overflow-hidden",
                      activeTab === item.id 
                        ? "bg-slate-900 text-white shadow-lg shadow-slate-200" 
                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                    )}
                  >
                    <div className={cn(
                      "p-2 rounded-xl transition-colors duration-300",
                      activeTab === item.id ? "bg-white/10" : item.bgColor
                    )}>
                      <item.icon size={18} className={cn(
                        "transition-colors duration-300",
                        activeTab === item.id ? "text-white" : item.color
                      )} />
                    </div>
                    <span className="text-sm font-semibold tracking-tight">{item.label}</span>
                  </button>
                ))}
              </nav>

              <div className="p-6 border-t border-slate-50 bg-slate-50/50">
                {showInstallButton && (
                  <button
                    onClick={handleInstallClick}
                    className="flex items-center w-full gap-3 px-4 py-3 mb-3 bg-indigo-600 text-white rounded-2xl transition-all duration-300 font-bold text-sm shadow-lg shadow-indigo-100"
                  >
                    <div className="p-2 bg-white/20 rounded-xl">
                      <Smartphone size={16} />
                    </div>
                    <span>התקן אפליקציה</span>
                  </button>
                )}
                <div className="flex items-center gap-3 px-4 py-3 mb-3 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold shadow-md shadow-indigo-100">
                    {(user.email?.[0] || 'U').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-black text-slate-800 truncate uppercase tracking-wider">{user.email?.split('@')[0]}</p>
                    <p className="text-[10px] font-medium text-slate-400 truncate">{user.email}</p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all duration-300 font-bold text-sm group"
                >
                  <div className="p-2 bg-red-50 text-red-500 rounded-xl group-hover:bg-red-500 group-hover:text-white transition-all duration-300">
                    <LogOut size={16} />
                  </div>
                  <span>התנתקות מהמערכת</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="lg:pr-72 pt-4 lg:pt-0 pb-24 lg:pb-0">
        {/* Desktop Top Header Bar */}
        <header className="hidden lg:flex items-center justify-between px-8 py-3.5 bg-white/90 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-xs mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-black text-slate-800 tracking-tight">
              {filteredMenuItems.find(m => m.id === activeTab)?.label || 'מערכת ניהול בית ספר'}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <AcademicYearSelector />
          </div>
        </header>

        <div className="max-w-7xl mx-auto p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Bottom Navigation for Mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-slate-100 px-2 py-2 flex items-center justify-around z-50 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] pb-safe">
        {filteredMenuItems.slice(0, 4).map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={cn(
              "flex flex-col items-center gap-1 px-3 py-1 rounded-2xl transition-all active:scale-95",
              activeTab === item.id ? "text-indigo-600" : "text-slate-400"
            )}
          >
            <div className={cn(
              "p-2 rounded-xl transition-all",
              activeTab === item.id ? "bg-indigo-50 shadow-sm" : "bg-transparent"
            )}>
              <item.icon size={22} strokeWidth={activeTab === item.id ? 2.5 : 2} />
            </div>
            <span className={cn(
              "text-[10px] font-bold tracking-tight",
              activeTab === item.id ? "opacity-100" : "opacity-70"
            )}>
              {item.label.split(' ')[0]}
            </span>
          </button>
        ))}
        <button
          onClick={() => setIsMenuOpen(true)}
          className="flex flex-col items-center gap-1 px-3 py-1 text-slate-400 active:scale-95"
        >
          <div className="p-2 rounded-xl">
            <Menu size={22} />
          </div>
          <span className="text-[10px] font-bold opacity-70">תפריט</span>
        </button>
      </nav>
    </div>
  );
};

export default Layout;
